/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * Modified by TMC.
 */

// TODO: CREW-8076 ファイル名の生成方法について、さらなる検討が必要
// https://break-tmc.atlassian.net/browse/CREW-8076

import { SimpleDateFormat } from '@crew/enums/system'
import dayjs from '@crew/modules'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { eventFiles } from '@lexical/rich-text'
import {
  COMMAND_PRIORITY_LOW,
  PASTE_COMMAND,
  DROP_COMMAND,
  PasteCommandType,
} from 'lexical'
import { useEffect, useCallback } from 'react'

import { UPLOAD_FILE_COMMAND } from '../fileUploadPlugin'
import { FileExtensions } from '@crew/enums/app'
import { useTranslation } from '@crew/modules/dist/i18n'
import { useToast } from 'hooks/useToast'

/**
 * ファイル名を生成する。file.nameに拡張子が含まれている場合は拡張子のみそのまま使う。
 * @param file
 * @param serial 指定するとファイル名に番号が付与される
 */
const generateFileName = (file: File, serial?: number) => {
  // ファイル名に拡張子が含まれている場合はそのまま使う
  const fileExt = file.name.split('.').pop()
  const ext = fileExt !== file.name ? fileExt : FileExtensions.Png

  return `Clipboard_${dayjs().format(SimpleDateFormat.YYYYMMDDHHmmss)}${
    serial ? `_${serial}` : ''
  }.${ext}`
}

/**
 * Drop/Pasteされたファイルの情報を保持する型
 */
type FileEntry = {
  file: File
  /**
   * 実際のファイル/ディレクトリであるかどうか。
   * ファイルやディレクトリをDrop/Pasteした場合はtrue、ClipboardのBitmapなどを貼り付けた場合はfalseになる
   */
  isRealFile: boolean
}

export default function DragDropPastePlugin(): null {
  const [editor] = useLexicalComposerContext()
  const toast = useToast()
  const [t] = useTranslation()

  // Paste,Drop時の処理
  const handlePasteOrDrop = useCallback(
    (event: DragEvent | PasteCommandType): boolean => {
      const [, , hasTextContent] = eventFiles(event)

      // ペーストした内容にテキストが含まれる場合はファイル貼り付けではない
      if (hasTextContent) {
        return false
      }

      // dataTransferが存在しない場合はファイル貼り付けではない
      if (!('dataTransfer' in event)) {
        return false
      }

      // dataTransfer.itemsからファイルのみ抽出する
      const entries = Array.from(event.dataTransfer?.items ?? [])
        .map((item) => {
          // 実際のファイル/ディレクトリの場合はwebkitGetAsEntryでentryがとれる。
          // とれないものは実際のファイル/ディレクトリではない(クリップボード内のデータやビットマップなど)
          const entry = item.webkitGetAsEntry()

          if (entry?.isDirectory) {
            // ディレクトリはアップロードできない
            toast.error(t('message.file.unsupportedFileFormat'))
            return null
          }

          const file = item.getAsFile()
          if (!file) {
            return null
          }

          return {
            file,
            isRealFile: entry !== null,
          } satisfies FileEntry
        })
        .filter((entry): entry is FileEntry => entry !== null)

      // ファイルが存在しない場合はファイルのPaste/Dropではない
      if (entries.length === 0) {
        return false
      }

      // ファイルのPaste/Dropである。アップロード処理を行う

      // デフォルトの処理をキャンセルしないと、Dropしたファイルがブラウザで開かれてしまう
      event.preventDefault()

      entries.forEach((entry, index) => {
        const filename = entry.isRealFile // 実際のファイルならファイル名をそのまま使う。そうでない場合はファイル名を生成する。
          ? entry.file.name
          : generateFileName(
              entry.file,
              // 複数エントリに対してファイル名生成する場合、名前が被らないようインデックス(1ベース)を付与する
              entries.length > 1 ? index + 1 : undefined
            )

        editor.dispatchCommand(UPLOAD_FILE_COMMAND, {
          file: new File([entry.file.slice()], filename, {
            type: entry.file.type,
          }),
        })
      })

      return true
    },
    [editor, t, toast]
  )

  // Paste時の処理
  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      handlePasteOrDrop,
      COMMAND_PRIORITY_LOW
    )
  }, [editor, handlePasteOrDrop])

  // Drop時の処理
  useEffect(() => {
    return editor.registerCommand(
      DROP_COMMAND,
      handlePasteOrDrop,
      COMMAND_PRIORITY_LOW
    )
  }, [editor, handlePasteOrDrop])

  return null
}
